import React, { createContext, useContext, useState, useCallback } from "react";
import ConfirmDialog from "@/components/ConfirmDialog";

const ConfirmCtx = createContext(null);

/**
 * Provider that mounts a single ConfirmDialog at the root and exposes
 * `useConfirm()` returning a function that resolves to a boolean.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title, description, confirmLabel })) { ... }
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });

  const ask = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        ...opts,
        _resolve: resolve,
      });
    });
  }, []);

  const close = (value) => {
    state._resolve?.(value);
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.title}
        description={state.description}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        tone={state.tone}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}
